import routes from '../routes/routes';
import { getActiveRoute, getActivePathname } from '../routes/url-parser';
import HomePresenter from './home/home-presenter';
import NearbyPresenter from './nearby/nearby-presenter';
import AuthPresenter from './auth/auth-presenter';
import ProfilePresenter from './profile/profile-presenter';
import BookmarksPresenter from './bookmarks/bookmarks-presenter';
import NotificationsPresenter from './notifications/notifications-presenter';
import PrivacyPresenter from './privacy/privacy-presenter';
import CookiesPresenter from './cookies/cookies-presenter';
import ThemeService from '../services/theme-service';
import AuthService from '../services/auth-service';
import * as UIService from '../services/ui-service';

class App {
  constructor() {
    this.currentPage = 'home';
    this.presenters = {};
    this.init();
  }

  async init() {
    AuthService.load();
    ThemeService.load();
    this.setupGlobalEventListeners();
    this.setupHistoryManagement();
    ThemeService.apply();
    await this.renderCurrentPage();
  }


  setupGlobalEventListeners() {
    document.querySelectorAll('.nav-link').forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        const page = e.currentTarget.dataset.page;
        this.navigateTo(page);
      });
    });

    const homeLink = document.querySelector('#home-link');
    if (homeLink) {
      homeLink.addEventListener('click', e => {
        e.preventDefault();
        this.navigateTo('home');
      });
    }

    document.querySelectorAll('.theme-selector, .setting-option[data-theme]').forEach(btn => {
      btn.addEventListener('click', e => {
        const theme = e.currentTarget.dataset.theme;
        if (theme) this.setTheme(theme);
      });
    });

    document.querySelectorAll('.font-selector, .setting-option[data-font]').forEach(btn => {
      btn.addEventListener('click', e => {
        const font = e.currentTarget.dataset.font;
        if (font) this.setFont(font);
      });
    });
  }

  setupHistoryManagement() {
    window.addEventListener('hashchange', () => this.handleRouteChange());
  }

  handleRouteChange() {
    const pathname = getActivePathname();
    if (pathname.startsWith('/post/')) {
      const postId = pathname.replace('/post/', '');
      this.navigateToPost(postId);
      return;
    }
    const route = getActiveRoute();
    const page = route === '/' ? 'home' : route.substring(1);
    this.navigateTo(page);
  }

  async navigateTo(page) {
    if (this.currentPage === page) return;
    this.currentPage = page;
    this.updateNavigationState(page);
    this.updatePageTitle(page);
    if (page === 'home') {
      window.location.hash = '/';
    } else if (page !== '404') {
      window.location.hash = `/${page}`;
    }
    await this.renderCurrentPage();
    window.scrollTo(0, 0);
  }

  async navigateToPost(postId) {
    this.currentPage = 'post';
    this.updatePageTitle('post');
    window.location.hash = `/post/${postId}`;
    await this.loadSinglePost(postId);
    window.scrollTo(0, 0);
  }

  async renderCurrentPage() {
    const container = document.querySelector('#main-content-area');
    if (!container) return;
    if (this.currentPage === '404') {
      this.show404Page();
      AuthService.updateAuthUI();
      return;
    }
    const routeMap = {
      home: '/',
      nearby: '/nearby',
      notifications: '/notifications',
      bookmarks: '/bookmarks',
      profile: '/profile',
      auth: '/auth',
      privacy: '/privacy',
      cookies: '/cookies'
    };
    const route = routeMap[this.currentPage];
    const page = routes[route];
    if (page) {
      container.innerHTML = await page.render();
      await page.afterRender();
      await this.initializePresenter(this.currentPage);
    } else {
      this.show404Page();
    }
    AuthService.updateAuthUI();
  }

  async initializePresenter(page) {
    if (this.presenters[page] && this.presenters[page].cleanup) {
      this.presenters[page].cleanup();
    }
    this.presenters[page] = null;
    const iface = this.createAppInterface();
    switch (page) {
      case 'home':
        this.presenters.home = new HomePresenter(null, routes['/'], iface);
        await this.presenters.home.init();
        break;
      case 'nearby':
        this.presenters.nearby = new NearbyPresenter(null, routes['/nearby'], iface);
        await this.presenters.nearby.init();
        break;
      case 'auth':
        this.presenters.auth = new AuthPresenter(null, routes['/auth'], iface);
        await this.presenters.auth.init();
        break;
      case 'profile':
        this.presenters.profile = new ProfilePresenter(null, routes['/profile'], iface);
        await this.presenters.profile.init();
        break;
      case 'bookmarks':
        this.presenters.bookmarks = new BookmarksPresenter(null, routes['/bookmarks'], iface);
        await this.presenters.bookmarks.init();
        break;
      case 'notifications':
        this.presenters.notifications = new NotificationsPresenter(null, routes['/notifications'], iface);
        await this.presenters.notifications.init();
        break;
      case 'privacy':
        this.presenters.privacy = new PrivacyPresenter(routes['/privacy'], iface);
        await this.presenters.privacy.init();
        break;
      case 'cookies':
        this.presenters.cookies = new CookiesPresenter(routes['/cookies'], iface);
        await this.presenters.cookies.init();
        break;
    }
  }

  createAppInterface() {
    return {
      showToast: (message, type, duration) => this.showToast(message, type, duration),
      showDialog: id => this.showDialog(id),
      hideDialog: id => this.hideDialog(id),
      showLightbox: src => this.showLightbox(src),
      showConfirmDialog: (msg, onConfirm) => this.showConfirmDialog(msg, onConfirm),
      navigateTo: page => this.navigateTo(page),
      navigateToPost: id => this.navigateToPost(id),
      navigateBack: () => this.navigateBack(),
      getCurrentPage: () => this.currentPage,
      isAuthenticated: () => AuthService.isAuthenticated,
      getUser: () => AuthService.user,
      setAuthState: (auth, user, token) => this.setAuthState(auth, user, token),
      setTheme: theme => this.setTheme(theme),
      setFont: font => this.setFont(font),
      setShareUrl: url => this.setShareUrl(url),
      updatePageTitle: title => this.updatePageTitle(title)
    };
  }

  updateNavigationState(currentPage) {
    document.querySelectorAll('.nav-link').forEach(link => link.classList.remove('active'));
    if (currentPage !== 'post' && currentPage !== '404') {
      const activeLink = document.querySelector(`.nav-link[data-page="${currentPage}"]`);
      if (activeLink) activeLink.classList.add('active');
    }
  }

  updatePageTitle(page) {
    const titles = {
      home: 'Beranda',
      nearby: 'Disekitar',
      notifications: 'Notifikasi',
      bookmarks: 'Tersimpan',
      profile: 'Profil',
      auth: 'Masuk',
      post: 'Postingan',
      privacy: 'Kebijakan Privasi',
      cookies: 'Kebijakan Kuki',
      404: 'Halaman Tidak Ditemukan'
    };
    const title = titles[page] || 'Halaman';
    const titleEl = document.querySelector('#page-title');
    if (titleEl) titleEl.textContent = title;
    document.title = `${title} - HeliaEDU`;
  }

  updateTheme(theme) {
    ThemeService.updateTheme(theme);
  }

  updateFont(font) {
    ThemeService.updateFont(font);
  }
  showToast(message, type = 'success', duration = 3000) {
    UIService.showToast(message, type, duration);
  }

  setTheme(theme) {
    ThemeService.setTheme(theme);
  }

  setFont(font) {
    ThemeService.setFont(font);
  }

  setAuthState(isAuth, user = {}, token = null) {
    AuthService.setAuthState(isAuth, user, token);
  }

  showDialog(id) {
    UIService.showDialog(id);
  }

  hideDialog(id) {
    UIService.hideDialog(id);
  }

  showLightbox(src) {
    UIService.showLightbox(src);
  }

  hideLightbox() {
    UIService.hideLightbox();
  }

  setShareUrl(url) {
    UIService.setShareUrl(url);
  }

  navigateBack() {
    if (window.history && window.history.length > 1) {
      window.history.back();
    } else {
      this.navigateTo('home');
    }
  }

  showConfirmDialog(message, onConfirm) {
    UIService.showConfirmDialog(message, onConfirm);
  }

  loadSinglePost(postId) {
    const container = document.querySelector('#main-content-area');
    if (!container) return;
    container.innerHTML = `
      <div id="page-post" class="page-content active">
        <div class="empty-state">
          <span class="material-symbols-rounded">info</span>
          <h3>Post Detail</h3>
          <p>Post ID: ${postId} - Feature will be implemented by specific presenter</p>
        </div>
      </div>`;
  }

  show404Page() {
    const container = document.querySelector('#main-content-area');
    if (!container) return;
    container.innerHTML = `
      <div id="page-404" class="page-content active">
        <div class="empty-state">
          <span class="material-symbols-rounded" aria-hidden="true">error_outline</span>
          <h3>Halaman Tidak Ditemukan</h3>
          <p>Maaf, halaman yang Anda cari tidak dapat ditemukan.</p>
          <button type="button" class="post-submit" onclick="window.location.hash='/'" data-text_tip="Kembali ke beranda">
            <span class="material-symbols-rounded" aria-hidden="true">home</span>
            <span>Kembali ke Beranda</span>
          </button>
        </div>
      </div>`;
  }


  cleanup() {
    Object.values(this.presenters).forEach(p => { if (p && p.cleanup) p.cleanup(); });
  }
}

export default App;

